// v220 admin network settings API client (item 24 of
// PRODUCT-ROADMAP.md, "Air-Gapped-Mode als Default-Installation").
//
// Two surfaces wrapped here:
//   GET /api/admin/network — current settings + recently blocked
//                            egress attempts.
//   PUT /api/admin/network — replace settings.
//
// The unauthenticated badge on /api/public-config (rendered in the
// AppShell topbar) reads `air_gapped_mode` directly from the public-
// config envelope and does not need this client.

import useSWR from 'swr';

export interface NetworkSettings {
  air_gapped_mode: boolean;
  allow_service_pings: boolean;
  allow_webhooks: boolean;
  allow_telemetry: boolean;
  allow_sigstore_rekor: boolean;
  allow_osv_feed: boolean;
  allow_otel_export: boolean;
  allow_scanner_db: boolean;
  updated_at: string;     // RFC3339
  updated_by?: string;
}

export interface EgressAuditRow {
  id: number;
  kind: string;            // 'webhook' | 'telemetry' | 'sigstore_rekor' | 'osv_feed' | 'otel_export' | 'scanner_db'
  source: string;
  target_url: string;
  reason: string;
  blocked_at: string;      // RFC3339
}

export interface NetworkResponse {
  settings: NetworkSettings;
  recent_blocked: EgressAuditRow[] | null;
}

const json = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useNetworkSettings() {
  return useSWR<NetworkResponse>('/api/admin/network', json, {
    refreshInterval: 30_000,
    revalidateOnFocus: true,
  });
}

export async function setNetworkSettings(input: Omit<NetworkSettings, 'updated_at' | 'updated_by'>): Promise<{ settings: NetworkSettings }> {
  const res = await fetch('/api/admin/network', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}
